import { useMemo } from 'react';
import { FinancialServicesUIProvider } from '@shipstation/financial-services-ui-components';
import { createIntl, createIntlCache, RawIntlProvider, useIntl } from 'react-intl';

import type { FinancialServicesUIProviderProps } from '@shipstation/financial-services-ui-components';
import type { ReactNode } from 'react';
import type { IntlShape } from 'react-intl';

const cache = createIntlCache();
const noop = () => undefined;

type AppIntlBridgeProps = {
  appIntl: IntlShape;
  children: ReactNode;
};

/** Merges the library's messages under the app's, keeping the app's locale, rich-text elements and error handling. */
const AppIntlBridge = ({ appIntl, children }: AppIntlBridgeProps) => {
  const libraryIntl = useIntl();

  const mergedIntl = useMemo(() => {
    // react-intl types messages as all-string or all-AST, but resolves each ID independently at runtime.
    const mergedMessages = { ...libraryIntl.messages, ...appIntl.messages } as IntlShape['messages'];
    // The app's messages are intentionally not pre-compiled, so formatjs's pre-compile warning is noise here.
    return createIntl({ ...appIntl, messages: mergedMessages, onWarn: noop }, cache);
  }, [appIntl, libraryIntl]);

  return <RawIntlProvider value={mergedIntl}>{children}</RawIntlProvider>;
};

type FinancialServicesUIIntlProviderProps = Omit<FinancialServicesUIProviderProps, 'locale'> & {
  /** Defaults to the surrounding app's locale. */
  locale?: string;
};

/**
 * Drop-in replacement for `FinancialServicesUIProvider`. The library wraps its children in its own
 * IntlProvider holding only library messages, which hides the app's messages from everything inside
 * it — app strings fall back to their English defaultMessage in every locale. This passes the app's
 * locale to the library and re-provides the app's intl, with the library's messages merged in, so
 * both app and library strings resolve.
 */
export const FinancialServicesUIIntlProvider = ({ children, locale, ...props }: FinancialServicesUIIntlProviderProps) => {
  const appIntl = useIntl();

  return (
    <FinancialServicesUIProvider {...props} locale={locale ?? appIntl.locale}>
      <AppIntlBridge appIntl={appIntl}>{children}</AppIntlBridge>
    </FinancialServicesUIProvider>
  );
};
